import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import omitBy from 'lodash/omitBy'
import { createSelector } from 'reselect'

import { extractVariablesFromMessageTemplate } from 'apps/messenger/utils/extractVariablesFromMessageTemplate'
import { isMessageTemplateHeaderValid } from 'apps/messenger/utils/isMessageTemplateHeaderValid'
import { sanitizeMessageTemplateDraft } from 'apps/messenger/utils/sanitizeMessageTemplateDraft'
import { validateMessageTemplate } from 'apps/messenger/utils/validateMessageTemplate'
import { WaTemplateUiMode } from 'apps/whatsApp/constants/messageTemplateConstants'
import { alert } from 'common/core'
import { getUserRole } from 'common/userRoles'
import { UserRoles } from 'common/userRoles/userRolesInterfaces'
import { MessengerApi } from 'shared/api/requests/messenger'
import {
  MessengerMessageTemplate,
  MessengerMessageTemplateCategoryId,
  MessengerMessageTemplateDraft,
  MessengerMessageTemplatePack,
  MessengerMessageTemplateStatus,
} from 'shared/api/requests/messenger/schemas'
import { createAsyncAction } from 'shared/lib/redux'

import { checkIfHeaderIsValid } from '../utils/helpers'

import type { MessengerCurrentMessageTemplateState } from './types'

export const initialState: MessengerCurrentMessageTemplateState = {
  loading: false,
  storedTemplates: {},
  drafts: {},
  activeLanguage: null,
  defaultLanguage: null,
  category: MessengerMessageTemplateCategoryId.UTILITY,
  humanName: null,
}

export const getTemplate = createAsyncAction<MessengerMessageTemplatePack | null, string>(
  'messenger/currentMessageTemplate/get',
  async (name) => {
    const result = await MessengerApi.getMessageTemplatePack({ query: { name } })
    return result.data.message_template
  },
)
export const createMessageTemplate = createAsyncAction<MessengerMessageTemplate | null>(
  'messenger/currentMessageTemplate/create',
  async (_, { getState }) => {
    const payload = getCurrentTemplateCreatePayload(getState())

    const result = await MessengerApi.createMessageTemplate({
      body: payload as MessengerMessageTemplate,
    })
    return result.data.message_template
  },
  {
    onBusinessError: (error) => {
      if (MessengerApi.createMessageTemplate.isExpectedMessageTemplateError(error)) {
        error.handle()
        alert(error.message, 'danger')
      }
    },
  },
)

export const currentMessageTemplateSlice = createSlice({
  name: 'messenger/currentMessageTemplate',
  initialState,
  reducers: {
    setHumanName: (state, action: PayloadAction<string>) => {
      state.humanName = action.payload || null

      if (state.activeLanguage && state.drafts[state.activeLanguage]) {
        state.drafts[state.activeLanguage].name_human = action.payload
      }
    },
    setCategory: (state, action: PayloadAction<MessengerMessageTemplateCategoryId | null>) => {
      state.category = action.payload

      if (state.activeLanguage && state.drafts[state.activeLanguage] && action.payload) {
        state.drafts[state.activeLanguage].category = action.payload
      }
    },
    setActiveLanguage: (state, action: PayloadAction<string | null>) => {
      state.activeLanguage = action.payload
    },
    updateTemplateDraft: (state, action: PayloadAction<MessengerMessageTemplateDraft>) => {
      if (!state.activeLanguage || !state.drafts[state.activeLanguage]) return

      state.drafts[state.activeLanguage] = sanitizeMessageTemplateDraft(
        action.payload,
      ) as MessengerMessageTemplateDraft
    },
    addLanguage: (state, action: PayloadAction<string>) => {
      state.drafts[action.payload] = { language_code: action.payload }

      if (state.category) {
        state.drafts[action.payload].category = state.category
      }

      if (state.humanName) {
        state.drafts[action.payload].name_human = state.humanName
      }

      state.activeLanguage = action.payload
    },
    deleteLanguage: (state, action: PayloadAction<string>) => {
      if (!state.drafts[action.payload]) return

      delete state.drafts[action.payload]
    },
    clearCurrentTemplate: () => {
      return initialState
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getTemplate.pending, (state) => {
      state.loading = true
    })

    builder.addCase(getTemplate.fulfilled, (state, action) => {
      if (!action.payload || action.payload.length === 0) {
        state.storedTemplates = {}
        state.drafts = {}
      } else {
        const templatesByLanguage = action.payload.reduce(
          (acc, template) => {
            acc[template.language_code] = template
            return acc
          },
          {} as Record<string, MessengerMessageTemplate>,
        )

        state.storedTemplates = templatesByLanguage
        state.drafts = templatesByLanguage

        const defaultTemplate = action.payload.find((template) => template.is_default)
        state.defaultLanguage = defaultTemplate
          ? defaultTemplate.language_code
          : action.payload[0].language_code

        state.humanName = action.payload[0].name_human
        state.category = action.payload[0].category
      }

      state.loading = false
    })

    builder.addCase(createMessageTemplate.fulfilled, (state, action) => {
      if (!action.payload) return

      state.storedTemplates[action.payload.language_code] = cloneDeep(action.payload)
      state.drafts[action.payload.language_code] = cloneDeep(action.payload)
    })
  },
})

export const getCurrentTemplateActiveLanguage = (state: RootState) =>
  state.messenger.currentMessageTemplate.activeLanguage

export const getCurrentTemplateHumanName = (state: RootState) =>
  state.messenger.currentMessageTemplate.humanName

export const getCurrentTemplateCategory = (state: RootState) =>
  state.messenger.currentMessageTemplate.category

export const getCurrentTemplateByLanguageSelector = (state: RootState) =>
  state.messenger.currentMessageTemplate.storedTemplates

export const getCurrentTemplateSelector = createSelector(
  (state: RootState) => state.messenger.currentMessageTemplate.storedTemplates,
  (state: RootState) => state.messenger.currentMessageTemplate.activeLanguage,
  (storedTemplates, activeLanguage) => {
    if (!storedTemplates || !activeLanguage) {
      return null
    }
    return storedTemplates[activeLanguage]
  },
)

export const getCurrentTemplateDraftsByLanguageSelector = (state: RootState) =>
  state.messenger.currentMessageTemplate.drafts

export const getCurrentTemplateDraftSelector = createSelector(
  (state: RootState) => state.messenger.currentMessageTemplate.drafts,
  (state: RootState) => state.messenger.currentMessageTemplate.activeLanguage,
  (drafts, activeLanguage): MessengerMessageTemplateDraft | null => {
    if (!drafts || !activeLanguage) {
      return null
    }
    return drafts[activeLanguage]
  },
)

export const getCurrentTemplateIsSendToReviewBlocked = createSelector(
  getCurrentTemplateDraftSelector,
  (state: RootState) => state.messenger.currentMessageTemplate.humanName,
  (state: RootState) => state.messenger.currentMessageTemplate.category,
  getUserRole,
  (template, humanName, category, userRole) => {
    if (!template) return true

    const { variables } = extractVariablesFromMessageTemplate(template)

    const areVariablesExamplesMissing = variables.some((variable) => !template?.example?.[variable])
    const hasPermissions = userRole === UserRoles.ADMIN || userRole === UserRoles.EDITOR

    const header = template?.header
    const isHeaderValid = checkIfHeaderIsValid(header)

    return Boolean(
      !category ||
        !humanName ||
        !template?.body ||
        areVariablesExamplesMissing ||
        !hasPermissions ||
        !isHeaderValid,
    )
  },
)

export const getCurrentTemplateErrors = createSelector(
  getCurrentTemplateDraftSelector,
  (state: RootState) => state.messenger.currentMessageTemplate.humanName,
  (draft, humanName) => {
    if (!draft) return {}

    return validateMessageTemplate(draft, {
      humanName: humanName || undefined,
    })
  },
)

export const getCurrentTemplateErrorsFlat = createSelector(getCurrentTemplateErrors, (errors) => {
  if (Object.keys(errors).length > 0) {
    return Object.values(errors).flat(1)
  }
  return []
})

export const getCurrentTemplateIsAllDataValid = createSelector(
  getCurrentTemplateErrors,
  (errors) => {
    return Object.keys(errors).length === 0
  },
)

export const getCurrentTemplateUiMode = createSelector(
  [getCurrentTemplateSelector, getCurrentTemplateDraftSelector],
  (template, draft) => {
    if (!draft?.status) return WaTemplateUiMode.CREATE
    if (
      draft.status === MessengerMessageTemplateStatus.PENDING ||
      draft.status === MessengerMessageTemplateStatus.DISABLED ||
      draft.status === MessengerMessageTemplateStatus.APPROVED ||
      draft.status === MessengerMessageTemplateStatus.REJECTED ||
      draft.status === MessengerMessageTemplateStatus.DELETED
    )
      return WaTemplateUiMode.VIEW_UNEDITABLE

    const isActiveLanguageTemplateHasUnsavedChanges = !isEqual(
      omitBy(template, (value) => value === '' || value === undefined),
      omitBy(draft, (value) => value === '' || value === undefined),
    )

    if (isActiveLanguageTemplateHasUnsavedChanges) return WaTemplateUiMode.EDIT

    return WaTemplateUiMode.VIEW_EDITABLE
  },
)

const getCurrentTemplateCommonPayload = createSelector(getCurrentTemplateDraftSelector, (draft) => {
  if (!draft) return {}
  const header =
    draft.header && isMessageTemplateHeaderValid(draft.header) ? draft.header : undefined
  const filteredButtons = draft.buttons?.filter((button) => Boolean(button.text))
  const buttons = filteredButtons && filteredButtons.length > 0 ? filteredButtons : undefined
  const example = filterDraftExampleForPayload(draft)

  return {
    header,
    buttons,
    example,
  }
})

const getCurrentTemplateCreatePayload = createSelector(
  [
    getCurrentTemplateDraftSelector,
    getCurrentTemplateCommonPayload,
    getCurrentTemplateCategory,
    getCurrentTemplateHumanName,
  ],
  (draft, payload, category, humanName): MessengerMessageTemplateDraft | null => {
    if (!draft) return null

    return {
      ...draft,
      ...payload,
      category: category || undefined,
      name_human: humanName || undefined,
    }
  },
)

export const getCurrentTemplateIsLoadingSelector = (state: RootState) =>
  state.messenger.currentMessageTemplate.loading

export const getCurrentTemplateIsEmpty = createSelector(
  (state: RootState) => state.messenger.currentMessageTemplate.drafts,
  (drafts) => Object.keys(drafts).length === 0,
)

export const getCurrentTemplateHasMoreThanOneLanguage = createSelector(
  (state: RootState) => state.messenger.currentMessageTemplate.storedTemplates,
  (storedTemplates) => Object.keys(storedTemplates).length > 1,
)

export const {
  setHumanName,
  setCategory,
  setActiveLanguage,
  updateTemplateDraft,
  addLanguage,
  deleteLanguage,
  clearCurrentTemplate,
} = currentMessageTemplateSlice.actions

export function filterDraftExampleForPayload(
  draft: MessengerMessageTemplateDraft,
): MessengerMessageTemplateDraft['example'] | undefined {
  if (!draft.example) return undefined

  const { variables } = extractVariablesFromMessageTemplate(draft)
  if (variables.length === 0) return undefined

  const extractedVariableNames = new Set(variables)
  const filteredExample = Object.fromEntries(
    Object.entries(draft.example).filter(([key, value]) => {
      return extractedVariableNames.has(key) && isNonEmptyString(value)
    }),
  )

  return Object.keys(filteredExample).length > 0 ? filteredExample : undefined
}

function isNonEmptyString(value: string | undefined): value is string {
  return typeof value === 'string' && value.trim() !== ''
}
